/**
 * Revenue — Revenue analytics and booking ROI
 * Data: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 *
 * Note: GA4 reports $0.00 revenue — e-commerce tracking is not configured.
 * This page shows the revenue gap and recommendations.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { BRAND, cssVar } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import PageHeader from '../shared/PageHeader';

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

const Revenue: React.FC = () => (
  <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
    <PageHeader title="Revenue" subtitle="Revenue analytics, booking value, and ROI analysis" />

    {/* Revenue Gap Alert */}
    <div className="bg-warning/5 border border-warning/30 rounded-xl p-6 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('GA4 reports $0.00 revenue — e-commerce tracking is not configured. What is the fastest path to fixing this and what revenue are we missing?', e)}>
      <div className="flex items-start gap-4">
        <span className="text-2xl text-warning flex-shrink-0">!!</span>
        <div>
          <p className="text-lg font-semibold text-text mb-2">Revenue Tracking Not Configured</p>
          <p className="text-sm text-text-3 leading-relaxed mb-4">
            GA4 Property 484092824 reports <span className="font-mono font-bold text-text">$0.00</span> total revenue for the 28-day period.
            E-commerce events are not firing from the booking widget. This means we cannot attribute revenue to marketing channels or measure true ROI.
          </p>
          <p className="text-sm text-text-2 font-medium">This is the #1 analytics gap to close.</p>
        </div>
      </div>
    </div>

    {/* What we do know */}
    <div className="bg-bg-1 border border-border rounded-xl p-6">
      <h3 className="font-display text-sm text-text-2 mb-4">What We Can Infer</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-3">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Booking Intent Signals</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">Reservation button clicks</span>
              <span className="text-sm font-mono font-bold text-text">13,000</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">Form submissions</span>
              <span className="text-sm font-mono font-bold text-text">118</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">Accommodations page views</span>
              <span className="text-sm font-mono font-bold text-text">7,000</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Estimated Revenue Impact</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">Est. avg nightly rate</span>
              <span className="text-sm font-mono font-bold text-text">$209*</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">If 118 submissions = bookings</span>
              <span className="text-sm font-mono font-bold text-success">~$49K</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-2">If funnel fixed to 5% rate</span>
              <span className="text-sm font-mono font-bold" style={{ color: BRAND.gold }}>~$271K</span>
            </div>
          </div>
          <p className="text-xs text-text-3 italic">* Based on Hilton Curio Collection San Antonio averages</p>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Channel ROI Ranking (by intent)</p>
          <div className="space-y-2">
            {[
              { channel: 'Referral', rate: '0.41%', color: cssVar('--color-success', '#10b981') },
              { channel: 'Paid Search', rate: '0.21%', color: cssVar('--color-success', '#10b981') },
              { channel: 'Paid Social', rate: '0.18%', color: cssVar('--color-warning', '#f59e0b') },
              { channel: 'Direct', rate: '0.11%', color: cssVar('--color-warning', '#f59e0b') },
              { channel: 'Organic Search', rate: '0.07%', color: cssVar('--color-danger', '#ef4444') },
            ].map((row) => (
              <div key={row.channel} className="flex items-center justify-between">
                <span className="text-xs text-text-2">{row.channel}</span>
                <span className="text-xs font-mono font-bold" style={{ color: row.color }}>{row.rate}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Action Items */}
    <div className="bg-bg-1 border border-border rounded-xl p-6">
      <h3 className="font-display text-sm text-text-2 mb-4">Revenue Tracking Action Plan</h3>
      <div className="space-y-4">
        {[
          {
            priority: 'P0',
            action: 'Implement e-commerce events in the booking widget',
            detail: 'Fire begin_checkout, add_to_cart, and purchase events from the Hilton/third-party booking engine. This unlocks all revenue attribution in GA4.',
            color: cssVar('--color-danger', '#ef4444'),
          },
          {
            priority: 'P0',
            action: 'Fix the reservation form drop-off',
            detail: 'Audit the path from reservation button click to form submission. Record sessions with Hotjar/FullStory to identify where 99.1% of users abandon.',
            color: cssVar('--color-danger', '#ef4444'),
          },
          {
            priority: 'P1',
            action: 'Set up Google Ads conversion import',
            detail: 'Once e-commerce events fire, import them as conversions in Google Ads to optimize Paid Search bidding for actual bookings.',
            color: cssVar('--color-warning', '#f59e0b'),
          },
          {
            priority: 'P1',
            action: 'Configure revenue attribution in GA4',
            detail: 'Enable data-driven attribution model to understand which channels contribute to revenue across the full customer journey.',
            color: cssVar('--color-warning', '#f59e0b'),
          },
          {
            priority: 'P2',
            action: 'Build monthly revenue reporting',
            detail: 'Once data flows, this page will auto-populate with channel ROI, booking trends, average booking value, and revenue per session.',
            color: cssVar('--color-info', '#3b82f6'),
          },
        ].map((item, idx) => (
          <div key={idx} className="flex items-start gap-4 p-3 rounded-lg hover:bg-bg-2 transition-colors cursor-pointer" onClick={(e) => askChat(`How do we implement: "${item.action}"? ${item.detail}`, e)}>
            <span
              className="text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 mt-0.5"
              style={{ backgroundColor: `${item.color}15`, color: item.color, border: `1px solid ${item.color}30` }}
            >
              {item.priority}
            </span>
            <div>
              <p className="text-sm font-medium text-text">{item.action}</p>
              <p className="text-xs text-text-3 mt-1 leading-relaxed">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);

export default Revenue;
